import z from "zod";
import type { Enums } from "../../../types/database.types";
import { CLASSIFYING_DOCUMENT_FILES_TABLES } from "../../../types/documents";

/**
 * The table name is an enum over the collections that carry a type column, narrower than the five
 * the read and upload routes accept. A collection with nothing to classify is a validation error
 * here: the update would match no column, and answering 200 to a request that changed nothing is
 * worse than refusing it.
 */
export const patchDocumentParamSchema = z.object({
	documentFilesTable: z.enum(CLASSIFYING_DOCUMENT_FILES_TABLES),
	documentId: z.string().uuid(),
});

/** The two classifications a document carries, as the database's own enum spells them. */
export const documentTypes = ["default", "risk-assessment"] as const satisfies ReadonlyArray<
	Enums<"documentType">
>;

/**
 * What a document patch may change: its classification, the re-acknowledgement interval a risk
 * assessment is asked for again after, or both. Every field is optional and the update writes only
 * the ones the body names, so setting one leaves the other as it stands.
 *
 * The interval is bounded at 1 and 120 months rather than left open, because both ends are
 * mistakes rather than choices: zero would ask for the document at every check-in and a decade is
 * indistinguishable from never. It is accepted on a `default` document too — the column is only
 * read for risk assessments, and a value set before a reclassification is already in place when the
 * document becomes one.
 *
 * A body naming neither field is refused: an empty patch is a request that means nothing, and an
 * update with an empty `SET` would answer 200 as though it had worked.
 */
export const patchDocumentBodySchema = z
	.object({
		type: z.enum(documentTypes).optional(),
		acknowledgementIntervalMonths: z.number().int().min(1).max(120).optional(),
	})
	.refine((body) => body.type !== undefined || body.acknowledgementIntervalMonths !== undefined, {
		message: "a document patch names its type, its acknowledgement interval, or both",
	});

export type PatchDocumentParams = z.infer<typeof patchDocumentParamSchema>;
export type PatchDocumentBody = z.infer<typeof patchDocumentBodySchema>;
